import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./i18n";
import "./styles/globals.css";

import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { HeroUIProvider } from "@heroui/react";
import { DarkModeProvider } from "@rbnd/react-dark-mode";
import { CookiesProvider } from "react-cookie";
import { Provider } from "react-redux";
import { MSALTenantProvider, MsalAuthAdapterBridge, PlatformProvider, useTenant } from "@VinciDiane/core";
import { ThemeProvider } from "@VinciDiane/ui";

import App from "./App.jsx";
import store from "./store/store";
import platformConfig from "./platform-config";
import { MsalAccountInitializer } from "./components/auth/MsalAccountInitializer";

const AppWrapper = () => {
  const { msalConfig } = useTenant();
  // msal-browser 5.x requires initialize() to resolve before the instance is
  // usable -- MsalProvider mounted with an uninitialized instance throws.
  const [msalInstance, setMsalInstance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const instance = new PublicClientApplication(msalConfig);

    instance.initialize()
      .then(() => {
        if (!cancelled) setMsalInstance(instance);
      })
      .catch((e) => console.error("MSAL initialize failed:", e));

    return () => { cancelled = true; };
  }, [msalConfig]);

  if (!msalInstance) return null;

  return (
    <DarkModeProvider>
      <ThemeProvider>
        <HeroUIProvider>
          <Provider store={store}>
            <MsalProvider instance={msalInstance}>
              <MsalAccountInitializer />
              <MsalAuthAdapterBridge>
                <App />
              </MsalAuthAdapterBridge>
            </MsalProvider>
          </Provider>
        </HeroUIProvider>
      </ThemeProvider>
    </DarkModeProvider>
  );
};

const container = document.getElementById('root');

// Cache root on container to avoid multiple createRoot calls
let root = container._reactRoot || null;

if (!root) {
  root = createRoot(container);
  container._reactRoot = root;
}

root.render(
  <PlatformProvider config={platformConfig}>
    <CookiesProvider>
      <MSALTenantProvider>
        <AppWrapper />
      </MSALTenantProvider>
    </CookiesProvider>
  </PlatformProvider>
);
